// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import {produce} from 'immer';
import i18next from 'i18next';

import {
  getCustomerInfo,
  getExperimentalPricingCurve,
  getExperimentPlanRecommendation,
  submitSelfServeQuoteChange,
} from 'media/js/pricing/api/api';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  BillingFrequency,
  BillingPeriod,
  CustomerType,
  ExperimentalCurvePoint,
  ExperimentalPricingCurve,
  OrgPlanInfo,
  PACKAGE_NAMES_BY_ID,
  PackageId,
  PlanState,
  PricingCurveId,
  PricingCurvePackage,
  PricingCurveVariant,
} from 'media/js/pricing/api/types';
import {LinkNode, LinkNodeStyle} from 'common/components/breadcrumbs/types';
import {formatDate} from 'media/js/pricing/plan-builder-2023/common/util';
import {SALES_TAX_DESCRIPTION} from 'media/js/pricing/plan-builder-2023/right-pane';
import {abbreviateNumber} from 'common/util';
import type {ChangeRequest, PurchaseRequest} from 'common/types/schema4api/billing/selfserve/types';
import {LocalizationStore} from 'common/i18n/store';
import {IronContextRegistry} from 'common/report/context-registry';

import {mixpanelTrack, TrackingEvents, TrackingPropertyNames} from '../tracking';
import {
  MAX_CARD_PAYMENT_THRESHOLD,
  PLAN_BUILDER_STEPS_BY_CUSTOMER_TYPE,
  PlanBuilderState,
  PlanBuilderStep,
  PlanBuilderStepId,
} from './types';
import {template} from './template';
import './index.styl';
import './step-components';
import './right-pane';

function getLocalStorageKey(type, accountId = null) {
  if (accountId) {
    return `plan-builder:${accountId}:${type}`;
  }
  return `plan-builder:unauthed:${type}`;
}

// there are no translations available for this app yet
i18next.init({
  lng: `en`,
  resources: {
    en: {
      translation: {},
    },
  },
});

export class PlanBuilder extends Component<PlanBuilderState, any, unknown, unknown, IronContextRegistry> {
  private readonly planChangeCacheKey = `plan-changes-pricing-2023`;
  private readonly stepIndexCacheKey = `step-index-pricing-2023`;

  static get attrsSchema(): AttrsSchema<any> {
    return {
      'is-pricing-2023-v1-1': {
        type: `boolean`,
        description: `for pricing 2023 GA's v1.1 pricing curve`,
      },
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
      'force-org-choice': {
        type: `boolean`,
        description: `true if customer manages multiple orgs and must choose one to edit`,
      },
    };
  }

  get config() {
    return {
      template,
      contexts: [`i18n` as const],
      defaultContexts: {
        i18n: new LocalizationStore(i18next),
      },
      defaultState: {
        allowPaymentViaCreditCard: true,
        customerInfo: {
          isAuthenticated: false,
          curveId: PricingCurveId.PRICING_2023,
          userInfo: {
            id: null,
            email: null,
            name: null,
          },
          orgInfo: {
            orgId: null,
            accountId: null,
            canDowngradeOrTransitionOnline: null,
            canPurchaseOnline: null,
            hasBillingPermissions: null,
            currentPlanState: {
              customerType: CustomerType.NONE,
              curveId: null,
              cap: null,
              billingFrequency: BillingFrequency.MONTHLY,
              packageIds: [],
              curveVariant: PricingCurveVariant.GROWTH,
              bumpCurveId: null,
            },
            orgName: null,
            mtuUsage: null,
            arr: null,
            creditsInfo: null,
            billingCredits: [],
            usageWasLastMonth: false,
            isOnBump: false,
            isBumpPbEnabled: false,
          },
        },
        pricingCurves: [],
        ready: false,
        selectedUpdate: {
          planChanges: {},
          paymentInfoChanges: {},
        },
        currentStepIdx: 0,
        // When true, the customer should see the thank you view
        showThankYou: false,
        amountDueInfo: null,
        isBreakdownExpanded: false,
        isAmountDueInfoLoading: false,
        dontUpdateAmountDue: false, // When true, skip updating the breakdown (e.g. mouse is down on slider)
        billingCard: null,
        showCurrentPlanPane: false,
        planRecommendation: null,
      },
      helpers: {
        // state/step helpers
        //TODO(P2023 goot): remove after full launch
        isP2023SeptGootEnabled: (): boolean => {
          return true;
        },
        isReady: (): boolean => {
          return this.state.ready;
        },
        getSelectedPlanState: (): PlanState => {
          return {
            ...this.state.customerInfo.orgInfo.currentPlanState,
            ...this.state.selectedUpdate.planChanges,
          };
        },
        getSteps: (): PlanBuilderStep[] | null => {
          // currentPlanState is readonly so the steps remain constant throughout the planbuilder experience
          let steps =
            PLAN_BUILDER_STEPS_BY_CUSTOMER_TYPE[this.state.customerInfo.orgInfo.currentPlanState.customerType];

          // should update PlanBuilderStep in types.ts
          if (this.attr(`mp-2023-style`)) {
            steps = produce(steps, (stepsDraft) => {
              stepsDraft.forEach((step) => {
                if (step.stepId === PlanBuilderStepId.PAYMENT_AUTHED) step.stepName = `Payment`;
              });
            });
          }
          return steps;
        },
        getCurrentStep: (): PlanBuilderStep | null => {
          const steps = this.helpers.getSteps();
          return steps ? steps[this.state.currentStepIdx || 0] : null;
        },
        getBreadcrumbs: (): LinkNode[] => {
          const steps = this.helpers.getSteps();
          return steps.map((step: PlanBuilderStep, i): LinkNode => {
            return {
              label: step.stepName,
              style: i === this.state.currentStepIdx ? LinkNodeStyle.Selected : LinkNodeStyle.Link,
            };
          });
        },
        handleBreadcrumbClick: (ev) => {
          const stepIndex = this.helpers.getSteps().findIndex((step) => step.stepName === ev.detail.node.label);
          this.update({currentStepIdx: stepIndex});
          this.helpers.trackStepChanged();
        },
        isAnnualDowngrade: (): boolean => {
          const isAnnualPlan =
            this.state.customerInfo.orgInfo.currentPlanState.billingFrequency === BillingFrequency.ANNUAL;
          const isDowngrade = this.state.amountDueInfo?.is_annual_downgrade;
          // TODO (Pricing 2023 cleanup) -  remove experimental plan check
          return isAnnualPlan && isDowngrade && this.state.customerInfo.orgInfo.isExperimentPlan;
        },
        isAddingPackage: (packageId: PackageId): boolean => {
          return !this.state.customerInfo.orgInfo.currentPlanState.packageIds.some((pkgId) => pkgId === packageId);
        },
        toggleBreakdownExpanded: () => {
          this.update({isBreakdownExpanded: !this.state.isBreakdownExpanded});
        },
        goToNextStep: (): void => {
          if (this.state.currentStepIdx + 1 < this.helpers.getSteps().length) {
            this.update({currentStepIdx: this.state.currentStepIdx + 1});
            window.localStorage.setItem(
              getLocalStorageKey(this.stepIndexCacheKey, this.state.customerInfo.orgInfo.accountId),
              this.state.currentStepIdx.toString(),
            );
            this.helpers.trackStepChanged();
          } else {
            this.helpers.resetStoredChanges();
            this.update({showThankYou: true});
          }
        },

        // Pricing/billing helpers
        checkIfIntegrated: (): boolean => {
          return this.state.customerInfo?.orgInfo?.mtuUsage && this.state.customerInfo?.orgInfo?.mtuUsage > 0;
        },
        getSelectedARR: (): number => {
          // multiplies getTotalPrice by 12 if monthly and 1 if annual to calculate ARR
          return this.helpers.getTotalPrice() * (12 / this.helpers.getSelectedPlanState().billingFrequency);
        },
        getBillingCycleString: (): string => {
          return this.helpers.getSelectedPlanState().billingFrequency === 12 ? `annual` : `monthly`;
        },
        getTotalText: (): string => {
          return this.state.currentStepIdx <= 1 ? `Subtotal` : `Total Due Today`;
        },
        processBumpDate: (date): Date => {
          const minuteToMillisecondMultiplier = 60000;
          return new Date(date.getTime() + date.getTimezoneOffset() * minuteToMillisecondMultiplier);
        },
        // This calculates the effective dates of the new plan
        calculateBillingPeriod: (): BillingPeriod => {
          if (this.state.amountDueInfo?.startDate && this.state.amountDueInfo?.renewalDate) {
            // If we can get the dates from the quote change endpoint, let's use them
            const renewalDate = new Date(this.state.amountDueInfo.renewalDate);
            const endDate = this.helpers.processBumpDate(renewalDate);
            const startDate = this.helpers.processBumpDate(new Date(this.state.amountDueInfo.startDate));

            return {startDate, endDate};
          }

          const startDate = new Date();
          let endDate = new Date();

          if (this.helpers.getSelectedPlanState().billingFrequency === BillingFrequency.MONTHLY) {
            endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
          } else {
            endDate = new Date(endDate.getFullYear() + 1, endDate.getMonth(), 0);
          }

          return {startDate, endDate};
        },
        getBillingPeriodString: (): string => {
          const {startDate, endDate} = this.helpers.calculateBillingPeriod();
          return `${formatDate(startDate)}–${formatDate(endDate)}`;
        },
        getSalesTaxAppliedAmount: (): number => {
          const lines = this.state?.amountDueInfo?.lines;
          if (!lines) {
            return 0;
          }
          const taxLine = lines.find((line) => line.description === SALES_TAX_DESCRIPTION);
          return taxLine ? parseFloat(taxLine.amount) : 0;
        },
        getPackagePrice: (packageId): number => {
          const featurePackage: PricingCurvePackage = this.helpers
            .getCurrentPricingCurve()
            ?.packages.find((featurePkg) => featurePkg.id === packageId);

          return this.helpers.getSelectedPlanPrice() * featurePackage?.basePlanPct;
        },
        getSelectedPaymentMethodId: (): string | null => {
          // If they're not updating their payment method, the one on file will be automatically used
          // by the backend.
          return this.state.selectedUpdate.paymentInfoChanges?.paymentMethod;
        },
        getSelectedCapString: (): string => {
          const cap = this.helpers.getSelectedCapAbbreviated();
          return `${cap} events`;
        },
        getSelectedCapAbbreviated: (): string => {
          const cap = this.helpers.getSelectedPlanState()?.cap;
          // Because we support decimal values now(12.5M events), we need to use a precision of 1 and remove if it has trailing 0's(30.0M -> 30M)
          return this.helpers.abbreviateNumber(cap);
        },
        abbreviateNumber: (n: number, precision = 1): string => {
          return abbreviateNumber(n, {precision, trimZeroes: true});
        },
        getSelectedPlanPrice: (): number => {
          const curvePoint = this.helpers.getSelectedPricingCurvePoint();
          return curvePoint?.price;
        },
        getSelectedPricingCurvePoint: (): ExperimentalCurvePoint | undefined => {
          return this.helpers.getCurrentPricingCurve()?.points[this.helpers.getSelectedPlanState()?.cap];
        },
        getCurrentPricingCurve: (): ExperimentalPricingCurve | undefined => {
          const {billingFrequency, curveVariant} = this.helpers.getSelectedPlanState();
          return this.helpers.getPricingCurveWithParams(billingFrequency, curveVariant);
        },
        getPricingCurveWithParams: (
          billingFrequency: BillingFrequency,
          curveVariant: PricingCurveVariant,
        ): ExperimentalPricingCurve | undefined => {
          return this.state.pricingCurves.find(
            (curve) =>
              curve.billingFrequency === billingFrequency &&
              (curveVariant === PricingCurveVariant.ENTERPRISE ? curve.isEnterprise : !curve.isEnterprise),
          ); // consider changing check to using enums rather than checking for boolean in case we get more curve variants
        },
        getOveragePricePerEventString: (): string | null => {
          const price = this.helpers.getTotalPrice();
          const {cap, billingFrequency, packageIds} = this.helpers.getSelectedPlanState();
          const curve = this.helpers.getCurrentPricingCurve();

          // Special case for when price is 0, use the specified overage rate
          // Still have to calculate in changes from add ons
          if (price <= 0) {
            const baseOverageRate = curve?.points[Object.keys(curve?.points)[0]]?.overageRate;
            if (!baseOverageRate) {
              return null;
            }
            let overageRateMultiplier = 1;
            curve?.packages.forEach((pkg) => {
              if (packageIds.includes(pkg.id)) {
                overageRateMultiplier += pkg.basePlanPct;
              }
            });
            const overageRate = (overageRateMultiplier * parseFloat(baseOverageRate)).toPrecision(3);
            const overageRateFormatted = overageRate.replace(/0+$/, ``); // no trailing 0s
            return `$${overageRateFormatted}/Event`;
          }

          if (!price || !cap || !billingFrequency || !curve) {
            return null;
          }

          const totalCap = cap * billingFrequency;
          const excludedQuantityFromOverage = (curve.excludedQuantityFromOverage || 0) * billingFrequency;
          const totalCapWithExclusions = Math.max(totalCap - excludedQuantityFromOverage, 0);
          const multiplier = curve.defaultOverageMultiplier || 1;

          const pricePerEvent = ((price / totalCapWithExclusions) * multiplier).toPrecision(3);
          const pricePerEventFormatted = pricePerEvent.replace(/0+$/, ``); // no trailing 0s
          return `$${pricePerEventFormatted}/Event`;
        },
        getTotalPrice: () => {
          const selectedPlanState = this.helpers.getSelectedPlanState();
          const packagesPrice = selectedPlanState.packageIds
            .map((packageId) => this.helpers.getPackagePrice(packageId))
            .reduce((accum, val) => accum + val, 0);

          return this.helpers.getSelectedPlanPrice() + packagesPrice;
        },
        getPackageIdsList: (): string[] => {
          return this.helpers.getSelectedPlanState().packageIds.map((id) => PACKAGE_NAMES_BY_ID[id]);
        },
        getOrgPlanInfo: (): OrgPlanInfo => {
          return this.state.customerInfo.orgInfo;
        },
        getRenewalDate: (): string => {
          const {endDate} = this.helpers.calculateBillingPeriod();
          const renewalDate = new Date(endDate.setDate(endDate.getDate() + 1));
          return formatDate(renewalDate);
        },
        // Handlers
        handleUpdateBreakdown: async (reset?: any) => {
          if (this.state.dontUpdateAmountDue) {
            return; // Skip updating breakdown
          }
          if (reset && !this.state.showCurrentPlanPane) {
            // clicking on step 1 and 2 will trigger this event
            // however if the customer already has a plan, we want to keep the breakdown open
            this.update({amountDueInfo: null});
          } else {
            // step 3 and 4 will call the quote change on connectedCallback
            this.update({isAmountDueInfoLoading: true});

            // There's a chance that the cap isn't updated correctly, if it's less than the minimum
            // value of the current curve, set it to the minimum value
            const minimumValueForCurve = Object.values(this.helpers.getCurrentPricingCurve()?.points || {})?.[0]?.cap;
            const selectedPlanState = this.helpers.getSelectedPlanState();
            if (selectedPlanState?.cap < minimumValueForCurve) {
              this.update({
                selectedUpdate: {
                  planChanges: {...this.state.selectedUpdate.planChanges, cap: minimumValueForCurve},
                  paymentInfoChanges: {...this.state.selectedUpdate.paymentInfoChanges},
                },
              });
            }

            const {...amountDueInfo} = await submitSelfServeQuoteChange(this.helpers.getChangeRequest());
            !this.helpers.isEnterpriseCustomer() && this.update({amountDueInfo});
            this.update({isAmountDueInfoLoading: false});
          }

          /**
           * Proactively enable/disable card payment here (vs. in the payment component)
           * to prevent sudden changes once that component is loaded, and since this called
           * on PB load at each step.
           * */
          const curveVariant = this.helpers.getSelectedPlanState().curveVariant;
          const totalPrice = this.helpers.getTotalPrice();

          // TODO (Pricing 2023 v1.1 cleanup): Remove this conditional and change the number for MAX_CARD_PAYMENT_THRESHOLD
          // max enterprise with both packages 1B monthly ENT plan = $127.800 * 1.6 = $204,480
          const MAX_CARD_PAYMENT_THRESHOLD_DURING_ROLLOUT = this.attr(`is-pricing-2023-v1-1`)
            ? 204480
            : MAX_CARD_PAYMENT_THRESHOLD;
          if (
            curveVariant === PricingCurveVariant.GROWTH ||
            (curveVariant === PricingCurveVariant.ENTERPRISE && totalPrice <= MAX_CARD_PAYMENT_THRESHOLD_DURING_ROLLOUT)
          ) {
            this.update({allowPaymentViaCreditCard: true});
          } else {
            this.update({allowPaymentViaCreditCard: false});
          }
        },
        handleChangeSelectedPlan: async (detail: Partial<PlanState>) => {
          this.update({
            selectedUpdate: {
              planChanges: {...this.state.selectedUpdate.planChanges, ...detail},
              paymentInfoChanges: {...this.state.selectedUpdate.paymentInfoChanges},
            },
          });

          window.localStorage.setItem(
            getLocalStorageKey(this.planChangeCacheKey, this.state?.customerInfo?.orgInfo?.accountId),
            JSON.stringify(this.state.selectedUpdate.planChanges),
          );

          if (this.state.amountDueInfo) {
            await this.helpers.handleUpdateBreakdown();
          }
        },
        handleChangeSelectedPaymentInfo: async (detail) => {
          this.update({
            selectedUpdate: {
              planChanges: {...this.state.selectedUpdate.planChanges},
              paymentInfoChanges: {...this.state.selectedUpdate.paymentInfoChanges, ...detail},
            },
          });
          if (this.state.amountDueInfo && this.state.selectedUpdate.paymentInfoChanges.postalCode) {
            await this.helpers.handleUpdateBreakdown();
          }
        },

        // Change/purchase request helpers
        getRequest: (): any => {
          const selectedState = this.helpers.getSelectedPlanState();
          const frequencyString = selectedState.billingFrequency === BillingFrequency.ANNUAL ? `annual` : `monthly`;
          const request = {
            billing_frequency: frequencyString,
            events_cap: selectedState?.cap?.toString(),
            packages: selectedState?.packageIds?.map((id) => PACKAGE_NAMES_BY_ID[id]),
            account_id: this.state?.customerInfo?.orgInfo?.accountId,
            new_pb: true,
            plan_id: this.helpers.getCurrentPricingCurve().planId,
            org_id: this.state.customerInfo.orgInfo.orgId,
            is_bump_request: this.state.customerInfo.orgInfo.isBumpPbEnabled || false, // if this is not set, default to false and let backend handle
          };
          if (this.state.selectedUpdate.paymentInfoChanges?.paymentMethod) {
            request[`payment_method_id`] = this.helpers.getSelectedPaymentMethodId();
          }
          if (this.state.selectedUpdate.paymentInfoChanges?.countryCode) {
            request[`country`] = this.state.selectedUpdate.paymentInfoChanges?.countryCode;
          }
          if (this.state.selectedUpdate.paymentInfoChanges?.postalCode) {
            request[`postal_code`] = this.state.selectedUpdate.paymentInfoChanges?.postalCode;
          }
          return request;
        },
        // These formats are identical
        getPurchaseRequest: (): PurchaseRequest => {
          return <PurchaseRequest>this.helpers.getRequest();
        },
        getChangeRequest: (): ChangeRequest => {
          return <ChangeRequest>this.helpers.getRequest();
        },

        // Tracking helpers
        getCommonTrackingProps: () => {
          return {
            'Is pricing 2023': true,
            [TrackingPropertyNames.PlanType]: this.helpers.getSelectedPlanState().curveVariant,
            [TrackingPropertyNames.BillingCycle]: this.helpers.getBillingCycleString(),
            [TrackingPropertyNames.ResourceType]: `Event`,
            [TrackingPropertyNames.SelectedVolume]: this.helpers.getSelectedPricingCurvePoint()?.cap,
            [TrackingPropertyNames.SelectedARR]: this.helpers.getSelectedARR(),
            [TrackingPropertyNames.PackagesProd]: this.helpers.getPackageIdsList(),
          };
        },
        resetStoredChanges: () => {
          const planChangesKey = getLocalStorageKey(
            this.planChangeCacheKey,
            this.state.customerInfo?.orgInfo?.accountId,
          );
          const stepIndexKey = getLocalStorageKey(this.stepIndexCacheKey, this.state.customerInfo?.orgInfo?.accountId);

          window.localStorage.removeItem(planChangesKey);
          window.localStorage.removeItem(stepIndexKey);
        },
        trackStepChanged: () => {
          mixpanelTrack(TrackingEvents.StepChanged, {
            ...this.helpers.getCommonTrackingProps(),
            [TrackingPropertyNames.SelectedStep]: this.state.currentStepIdx + 1, // currentStepIdx is 0 indexed
          });
        },
        trackEventWithPlanProps: (eventName, extraProps = {}) => {
          mixpanelTrack(eventName, {
            ...this.helpers.getCommonTrackingProps(),
            [TrackingPropertyNames.TaxQuoted]: this.helpers.getSalesTaxAppliedAmount(),
            [TrackingPropertyNames.PostalCode]: this.state.selectedUpdate.paymentInfoChanges?.postalCode,
            [TrackingPropertyNames.CountryCode]: this.state.selectedUpdate.paymentInfoChanges?.countryCode,
            [TrackingPropertyNames.AccountId]: this.state?.customerInfo?.orgInfo?.accountId,
            [TrackingPropertyNames.UserId]: this.state.customerInfo?.userInfo?.id,
            ...extraProps,
          });
        },
        isEnterpriseCustomer: () => {
          // Evaluates true for both self serve and contract enterprise customers
          return this.state.customerInfo.orgInfo?.currentPlanState?.curveVariant === PricingCurveVariant.ENTERPRISE;
        },
        isNewStartupProgramCustomer: () => {
          return this.state.customerInfo.orgInfo?.currentPlanState?.isNewStartupProgramCustomer;
        },
        trackContactSalesClicked: () => {
          this.helpers.trackEventWithPlanProps(TrackingEvents.ContactUs, {
            'Contact Us Source': window.location.href || `Plan Builder`,
          });
        },
      },
    };
  }

  public async connectedCallback() {
    super.connectedCallback();

    const stateUpdate: Partial<PlanBuilderState> = {};
    const customerInfo = await getCustomerInfo({isPricing2023: true});
    stateUpdate.customerInfo = this.config.defaultState.customerInfo;

    if (customerInfo.isAuthenticated) {
      stateUpdate.customerInfo = customerInfo;
    }

    const customerType: CustomerType =
      stateUpdate.customerInfo?.orgInfo?.currentPlanState?.customerType || CustomerType.NONE;

    const currentPlanCustomerTypes = [
      CustomerType.SELFSERVE_MTU,
      CustomerType.SELFSERVE_LEGACY,
      CustomerType.CONTRACT_MTU,
      CustomerType.CONTRACT_LEGACY,
      CustomerType.GENERIC_LEGACY,
      CustomerType.FREE_MTU_100K,
      CustomerType.FREE_MTU,
      CustomerType.FREE_PRICING_2023,
      CustomerType.PREMIUM_PRICING_2023,
    ];

    if (currentPlanCustomerTypes.includes(customerType)) {
      stateUpdate.showCurrentPlanPane = true;
    }

    // Recover stored plan changes and step index
    let storedPlanChanges,
      storedStepIndex = null;
    const planChangesKey = getLocalStorageKey(this.planChangeCacheKey, stateUpdate?.customerInfo?.orgInfo?.accountId);
    const stepIndexKey = getLocalStorageKey(this.stepIndexCacheKey, stateUpdate?.customerInfo?.orgInfo?.accountId);

    // Code to handle case where customer auths mid flow.
    // We copy the old, unauthed state into the new authed state
    if (stateUpdate?.customerInfo?.orgInfo?.accountId) {
      const unauthedPlanChangesKey = getLocalStorageKey(this.planChangeCacheKey);
      const unauthedPlanChanges = window.localStorage.getItem(unauthedPlanChangesKey);
      if (unauthedPlanChanges) {
        window.localStorage.setItem(planChangesKey, unauthedPlanChanges);
        window.localStorage.removeItem(unauthedPlanChangesKey);
      }
      const unauthedStepIndexKey = getLocalStorageKey(this.stepIndexCacheKey);
      const unauthedStepIndex = window.localStorage.getItem(unauthedStepIndexKey);
      if (unauthedStepIndex) {
        window.localStorage.setItem(stepIndexKey, unauthedStepIndex);
        window.localStorage.removeItem(unauthedStepIndexKey);
      }
      const planRecommendation = await getExperimentPlanRecommendation(stateUpdate.customerInfo.orgInfo.accountId);
      if (planRecommendation) {
        stateUpdate.planRecommendation = planRecommendation.recommendation;
      }
    }

    stateUpdate.pricingCurves = await getExperimentalPricingCurve(stateUpdate.customerInfo?.orgInfo);

    if (stateUpdate.planRecommendation) {
      stateUpdate.selectedUpdate = {
        planChanges: {
          cap:
            // TODO(P2023 GOOT): remove this after full launch
            this.helpers.isP2023SeptGootEnabled() && stateUpdate.planRecommendation.cap > 20000000
              ? 20000001
              : stateUpdate.planRecommendation.cap,
        },
        paymentInfoChanges: {},
      };
    }

    // otherwise, try to pre-populate with stored changes
    try {
      storedPlanChanges = JSON.parse(window.localStorage.getItem(planChangesKey));
      storedStepIndex = parseInt(window.localStorage.getItem(stepIndexKey));
    } catch {
      // pass
    }

    // Restore stored data. Note: we removed Enterprise plans from the
    // Plan Builder in Jan '24, so if a user's local storage has it
    // selected, we shouldn't load it (to avoid breaking the builder)
    if (storedPlanChanges && storedPlanChanges.pricingCurveVariant !== PricingCurveVariant.ENTERPRISE) {
      stateUpdate.selectedUpdate = {
        planChanges: storedPlanChanges,
        paymentInfoChanges: {},
      };
    }

    // select persisted step if exists, with guardrails
    if (
      storedStepIndex &&
      storedStepIndex > 0 &&
      storedStepIndex < PLAN_BUILDER_STEPS_BY_CUSTOMER_TYPE[customerType].length
    ) {
      this.state.currentStepIdx = storedStepIndex;
    }

    stateUpdate.ready = true;
    this.update(stateUpdate);

    mixpanelTrack(TrackingEvents.PlanBuilderLoaded, {
      ...this.helpers.getCommonTrackingProps(),
      [TrackingPropertyNames.SelectedStep]: this.state.currentStepIdx + 1, // currentStepIdx is 0 indexed
    });

    // Auto populate breakdown for authed customers
    if (this.state.customerInfo.orgInfo.currentPlanState.customerType !== CustomerType.NONE) {
      await this.helpers.handleUpdateBreakdown();
    }
  }
}

export const PlanBuilderV2 = defineAndMakeJsxFactory(`plan-builder-2023`, PlanBuilder);
